import React from 'react';
import styled from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import { Modal } from 'lib/components';
import { theme } from 'lib/style';
import { removeUserFromDepartment } from 'lib/api';
import { useToastError } from 'lib/hooks';
import { successToast } from 'lib/components/toasts/success';
import { Button } from 'react-covideo-common';
import { IoMdClose } from 'react-icons/io';
import { useTranslation } from 'react-i18next';

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  color: ${theme.palette.themeDark};

  font-style: normal;
  font-size: 18px;
  line-height: 24px;
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 600px;
  margin-top: 24px;
`;

const Footer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

type Props = {
  handleModalClose: (shouldRefresh?: boolean) => void;
  selectedUser: number[];
  departmentId?: any;
  customerId?: any;
};

export const RemoveUserFromDepartmentModal = (props: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);

  const { handleModalClose, selectedUser, departmentId, customerId } = props;

  const { ref } = useOnClickOutside(handleModalClose);

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };

  const { showError } = useToastError();

  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  const removeUserFromDept = async () => {
    setLoading(true);
    try {
      await removeUserFromDepartment(
        { userIds: selectedUser, departmentIds: departmentId },
        customerId
      );
      setLoading(false);
      successToast({ title: t('User(s) removed successfully!') });
      handleModalClose(true);
    } catch (error) {
      showError(error);
      setLoading(false);
    }
    setLoading(false);
  };

  return (
    <Modal style={{ position: 'fixed' }}>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>{t('Are you sure?')}</HeaderTitle>
          <CloseButtonWrap title={'Close'}>
            <IoMdClose
              size={24}
              onClick={() => handleModalClose()}
              color={theme.palette.label}
            />
          </CloseButtonWrap>
        </Header>
        <Content>
          {t('Selected users will be removed from the Department.')}
        </Content>
        <Footer>
          <Button
            text={t('No, Cancel')}
            variant='secondary'
            onClick={async () => {
              handleModalClose();
            }}
            disabled={loading}
          />
          <Button
            text={t('Yes, Remove')}
            variant='red'
            onClick={async () => {
              await removeUserFromDept();
            }}
            disabled={loading}
          />
        </Footer>
      </div>
    </Modal>
  );
};
